import type { ButtonProps, DialogContentProps, DialogProps, DialogTitleProps } from '@mui/material';
import { Dialog, DialogTitle, Typography, IconButton, DialogContent, Box, DialogActions, Button, DialogContentText } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { isNotNullOrUndefined } from '@weft/utils';
import type { PropsWithChildren } from 'react';
import React from 'react';

type VyDialogProps = PropsWithChildren<{
  open: boolean;
  label?: React.ReactNode;
  labelDescription?: React.ReactNode;
  warningText?: React.ReactNode;
  cancelButtonText?: React.ReactNode;
  ctaText?: React.ReactNode;
  ctaDisabled?: boolean;
  onSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  cancelButtonProps?: ButtonProps;
  ctaButtonProps?: ButtonProps;
  dialogTitleProps?: DialogTitleProps;
  warningColor?: string;
  dialogContentProps?: DialogContentProps;
}> & Omit<DialogProps, 'title'>;

const VyDialog: React.FC<VyDialogProps> = ({
  open,
  onClose,
  label,
  labelDescription,
  warningText,
  cancelButtonText,
  ctaText,
  ctaDisabled,
  onSubmit,
  warningColor,
  children,
  cancelButtonProps,
  ctaButtonProps,
  dialogTitleProps,
  dialogContentProps,
  ...dialogProps
}) => {
  const isDisabled = ctaDisabled ?? false;

  return (
    <Dialog
      open={open}
      maxWidth='md'
      fullWidth={false}
      {...dialogProps}
      PaperProps={{ 
        ...dialogProps.PaperProps,  
        sx: { borderRadius: '8px', ...dialogProps.PaperProps?.sx },  
      }}  
      onClose={onClose}
    >
      <DialogTitle 
        {...dialogTitleProps}
        sx={{
          padding: '24px',
          ...dialogTitleProps?.sx,
        }}
      >
        <Typography
          fontSize="24px"
          fontWeight={350}
          lineHeight="32px"
          paddingBottom="10px"
        >
          {label}
        </Typography>
         
        <IconButton
          onClick={() => {
            onClose?.({}, 'backdropClick');
          }}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <VyIcon icon="CloseBig"/>
        </IconButton>
        <Typography
          variant="body1"
          fontWeight={350}
          fontSize='16px'
          lineHeight="24px"
        >
          {labelDescription}
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        style={{ padding:'20px', inlineSize: '680px' }}
        {...dialogContentProps}
      >
        {children}

        <DialogContentText>
          {warningText && (
            <Box
              display="flex"
              gap={2}
            >
              <VyIcon
                icon="Warning"
                color={warningColor ?? 'black'}
              />
              <Typography color={warningColor ?? undefined} >
                {warningText}
              </Typography>
            </Box>
          )}
        </DialogContentText>
      </DialogContent>
  
      <DialogActions style={{
        padding: '24px',
        gap: '5px',
      }}
      >
        {isNotNullOrUndefined(cancelButtonText) && (
          <Button
            variant="contained"
            color="secondary"
            size='large'
            onClick={() => {
              onClose?.({}, 'backdropClick');
            }}
            {...cancelButtonProps}
          >
            {cancelButtonText}
          </Button>
        )}

        {isNotNullOrUndefined(ctaText) && (
          <Button
            variant="contained"
            color='primary'
            size='large'
            disabled={isDisabled}
            onClick={onSubmit}
            {...ctaButtonProps}
          >
            {ctaText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
  
export default VyDialog;